/* eslint-disable @typescript-eslint/no-unsafe-argument */

import { IEvent, IEventAggregationService } from '@studyportals/event-aggregation-service-interface';
import { ProfileQuestionnairesReadyEvent } from '@studyportals/profile-questionnaires-interfaces';
import { SessionServiceReadyEvent, WishlistServiceReadyEvent, SessionCreatedEvent } from '@studyportals/student-interfaces';
import { AuthenticationServiceReadyEvent } from '@studyportals/authentication-service-interfaces';
import { DecisionMakingServiceReadyEvent } from '@studyportals/decision-making-service-interfaces';
import {
	AnonymousStudentProfileSynced,
} from "@studyportals/anonymous-student-interfaces";
import "reflect-metadata";

type DependencyEntry = ProfileQuestionnairesReadyEvent |
SessionServiceReadyEvent |
SessionCreatedEvent |
WishlistServiceReadyEvent |
AuthenticationServiceReadyEvent |
DecisionMakingServiceReadyEvent |
AnonymousStudentProfileSynced |
IEvent;

type DependencyRecord = Record<string, DependencyEntry>;

// eslint-disable-next-line @typescript-eslint/naming-convention
const Inject = (eventType: Dependency) => {
	return (target: any, propertyKey: string, parameterIndex: number): void => {
		const existingParameters: unknown = Reflect.getOwnMetadata(propertyKey, target, propertyKey);
		const parameters: number[] = existingParameters as number[] || [];
		parameters.push(parameterIndex);
		Reflect.defineMetadata(propertyKey, parameters, target, propertyKey);

		const existingDependencies: unknown = Reflect.getOwnMetadata(propertyKey, target, `${propertyKey}_deps`);
		const dependencies: Dependency[] = existingDependencies as Dependency[] || [];
		dependencies[parameterIndex] = eventType;
		Reflect.defineMetadata(propertyKey, dependencies, target, `${propertyKey}_deps`);
	};
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const ResolveDependencies = (target: any, propertyKey: string, descriptor: PropertyDescriptor): void => {
	const originalMethod: (...args) => void = descriptor.value as (...args) => void;

	descriptor.value = async function (...args: unknown[]): Promise<void> {
		const metaParameters: unknown = Reflect.getOwnMetadata(propertyKey, target, propertyKey);
		const metaDependencies: unknown = Reflect.getOwnMetadata(propertyKey, target, `${propertyKey}_deps`);

		const parameterIndices: number[] = metaParameters as number[] || [];
		const dependencies: Dependency[] = metaDependencies as Dependency[] || [];

		for (const index of parameterIndices) {
			args[index] = await Dependencies.GetDependencyInstance(dependencies[index]);
		}

		originalMethod.apply(this, args);
	};
}

enum Dependency {
	PROFILE_QUESTIONNAIRES_SERVICE = ProfileQuestionnairesReadyEvent.EventType as unknown as Dependency,
	SESSION_SERVICE = SessionServiceReadyEvent.EventType as unknown as Dependency,
	WISHLIST_SERVICE = WishlistServiceReadyEvent.EventType as unknown as Dependency,
	AUTHENTICATION_SERVICE = AuthenticationServiceReadyEvent.EventType as unknown as Dependency,
	DECISION_MAKING_SERVICE = DecisionMakingServiceReadyEvent.EventType as unknown as Dependency,
	ANONYMOUS_STUDENT_PROFILE_SYNCED = AnonymousStudentProfileSynced.EventType as unknown as Dependency,
}

class Dependencies {
	private static readonly Promises: DependencyRecord = {} as DependencyRecord;

	public static async GetDependencyInstance(dependency: Dependency): Promise<IEvent> {
		if (Dependencies.Promises[dependency]) {
			return Dependencies.Promises[dependency];
		}

		Dependencies.Promises[dependency] = await Dependencies.DependencyPromiseCreator(dependency);

		return Dependencies.Promises[dependency];
	}

	private static DependencyPromiseCreator(dependency: Dependency): Promise<IEvent> {
		return new Promise((resolve) => {
			if (!Dependencies.EventAggregationService) {
				throw new Error("EventAggregationService is not available");
			}

			Dependencies.EventAggregationService.subscribeTo(dependency.toString(),
				{
					notify : (event: IEvent) => {
						resolve(event);
					}
				}, true
			);
		});
	}

	private static get EventAggregationService(): IEventAggregationService {
		return window.EventAggregationService;
	}
}

export {
	Inject,
	ResolveDependencies,
	Dependencies,
	Dependency,
}